// THE RED LINE'S ROUTE (2026-10-25, Chris: the red line told a different story from the Insights card). There is ONE definition of "the most common route", the walk in insights/pathCore.ts, answered
// by the server for a range (getCommonRoute) and worked out in the browser for a replay (routeTally.ts). This turns that answer, which names pages, into the red line's own shape, which names boxes:
// a page in a box is that box, a page that is in none is the "All other pages" box, and a run of pages in the same box is one stop on the canvas.
import type { PathAnswer } from "../insights/pathCore";
import type { EndReason, HappyPath } from "./happyPath";

const endReasonOf = (stop: PathAnswer["stop"]): EndReason => (stop === "ended" ? "no-next" : stop === "thin" ? "thin" : "max");

/** the route as the red line draws it, or null when there is none (no visits, or none that go anywhere). `boxOf` says which box a page is drawn in (null: it is not on the canvas at all). */
export const routeToHappyPath = (route: PathAnswer | null | undefined, boxOf: (page: string) => number | null): HappyPath | null => {
  if (!route || route.steps.length === 0) return null;
  const steps: number[] = [];
  const hops: HappyPath["hops"] = [];
  let last = route.steps[0];
  let revisited = false;
  for (const step of route.steps) {
    const box = boxOf(step.page);
    if (box === null) break;
    if (steps.length > 0 && steps[steps.length - 1] === box) {
      last = step;                                  // another page in the same box: the same stop on the canvas
      continue;
    }
    if (steps.includes(box)) { revisited = true; break; }   // back to a box the line has already been through: the line ends rather than loops
    if (steps.length > 0) hops.push({ from: steps[steps.length - 1], to: box, weight: step.reached });
    steps.push(box);
    last = step;
  }
  if (steps.length === 0) return null;

  return {
    steps,
    startWeight: route.steps[0].reached,
    totalArrivals: route.visits,
    hops,
    end: { index: steps[steps.length - 1], departed: last.ended, nextWeight: 0, nextIndex: null, reason: revisited ? "complete" : endReasonOf(route.stop) },
  };
};
